import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { ApiService } from '../../_services/api.service';
import { langCodeFromLocale } from '../../_helpers/utils';

@Component({
  selector: 'app-specialty-step',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatAutocompleteModule, MatButtonModule, TranslateModule, LoadingSpinnerComponent,
  ],
  host: { class: 'flex flex-col flex-1 min-h-0' },
  template: `
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col flex-1 min-h-0">
      <div class="flex-1 overflow-auto min-h-0">
        <h2 class="headline-medium text-primary mb-2 hidden md:block" style="font-family: 'DM Sans', sans-serif">{{ "What's your speciality?" | translate }}</h2>
        <p class="body-large text-on-surface-variant mb-6" style="font-family: 'DM Sans', sans-serif">{{ 'We use this information to help provide specific blueprints and suggestions based on your primary speciality' | translate }}</p>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>{{ 'Speciality' | translate }}</mat-label>
          <input
            matInput
            type="text"
            formControlName="speciality"
            [placeholder]="'Type to search specialities' | translate"
            [matAutocomplete]="auto"
            (input)="filterSpecialities()"
          />
          <mat-autocomplete #auto="matAutocomplete">
            @for (s of filteredSpecialities; track s) {
              <mat-option [value]="s">{{ s }}</mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </div>

      <div class="flex items-center justify-between gap-4 pt-4 shrink-0">
        <button mat-stroked-button type="button" class="w-1/2" (click)="remindLater.emit()">
          {{ 'Remind me later' | translate }}
        </button>

        <button
          mat-flat-button
          color="primary"
          type="submit"
          class="w-1/2"
          [disabled]="form.invalid || loading"
        >
          <app-loading-spinner [loading]="loading" [diameter]="20">
            {{ 'Continue' | translate }}
          </app-loading-spinner>
        </button>
      </div>
    </form>
  `,
})
export class SpecialtyStepComponent implements OnInit {
  @Input() loading = false;
  @Input() initialSpeciality = '';
  @Output() submitSpeciality = new EventEmitter<string>();
  @Output() remindLater = new EventEmitter<void>();

  form!: FormGroup;
  specialities: string[] = [];
  filteredSpecialities: string[] = [];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private translate: TranslateService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.form = this.fb.group({
      speciality: [this.initialSpeciality, [Validators.required, this.validSpecialityValidator()]],
    });
    try {
      this.specialities = await this.apiService.getSpecialities(langCodeFromLocale(this.translate.currentLang));
      this.filteredSpecialities = [...this.specialities];
      this.form.get('speciality')?.updateValueAndValidity();
    } catch {
      this.specialities = [];
      this.filteredSpecialities = [];
    }
  }

  private validSpecialityValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      return this.specialities.includes(control.value) ? null : { invalidSpeciality: true };
    };
  }

  filterSpecialities(): void {
    const value = this.form.get('speciality')?.value || '';
    const searchLower = value.toLowerCase();
    this.filteredSpecialities = this.specialities.filter(s =>
      s.toLowerCase().includes(searchLower)
    );
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    this.submitSpeciality.emit(this.form.value.speciality);
  }
}
